import { logger } from "@/lib/logger";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MaintenanceGate } from "@/components/maintenance-gate";
import { SiteFooter } from "@/components/site-footer";
import { registerPWA } from "@/lib/pwa-register";
import { initSentry } from "@/lib/sentry-init";
import { InstallPrompt } from "@/components/install-prompt";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  logger.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  // Detect database / connection-pool exhaustion so viral spikes show a friendly
  // "you're in the queue" message instead of a blank error screen.
  const msg = (error?.message ?? "").toLowerCase();
  const isOverloaded =
    msg.includes("too many connections") ||
    msg.includes("connection pool") ||
    msg.includes("remaining connection slots") ||
    msg.includes("server is busy") ||
    msg.includes("timeout") ||
    msg.includes("503") ||
    msg.includes("econnrefused");

  if (isOverloaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-2xl">
            ⏳
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            High demand detected. You're in the queue.
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Average wait: ~30 seconds. ForexMind AI is scaling to meet demand —
            this page will retry automatically.
          </p>
          <div className="mt-6">
            <button
              onClick={() => {
                router.invalidate();
                reset();
              }}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Retry now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#0a0e1a" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "ForexMind" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "google-site-verification", content: "169oTHu2Sl88vg6TeyWeOdDku6bIyBDF5yHaFgBYCNU" },
      { title: "ForexMind AI — AI Market Intelligence" },
      { name: "description", content: "Institutional-grade AI market intelligence. 11 specialist agents debate every setup. Educational insights only — not financial advice." },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "ForexMind AI — AI Market Intelligence" },
      { property: "og:description", content: "Institutional-grade AI market intelligence. 11 specialist agents debate every setup. Educational insights only — not financial advice." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@forexmindai" },
      { name: "twitter:title", content: "ForexMind AI — AI Market Intelligence" },
      { name: "twitter:description", content: "Institutional-grade AI market intelligence. 11 specialist agents debate every setup. Educational insights only — not financial advice." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "icon", href: "/icon-192.png", type: "image/png" },
      { rel: "icon", href: "/favicon-32.png", type: "image/png", sizes: "32x32" },
      { rel: "icon", href: "/favicon-16.png", type: "image/png", sizes: "16x16" },
      { rel: "icon", href: "/icon-192.png", type: "image/png", sizes: "192x192" },
      { rel: "icon", href: "/icon-512.png", type: "image/png", sizes: "512x512" },
      { rel: "shortcut icon", href: "/favicon-32.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      // LCP-critical assets: self-hosted Sora + navbar logo
      { rel: "preload", href: "/fonts/Sora-latin.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "preload", href: "/__l5e/assets-v1/0797a99e-caf1-4e40-9d66-dd3d051620e4/forexmind-mark.webp", as: "image", fetchPriority: "high" } as any,
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "ForexMind AI",
              url: "https://forexmind.ai",
              logo: "https://forexmind.ai/icon-512.png",
            },
            {
              "@type": "WebSite",
              name: "ForexMind AI",
              url: "https://forexmind.ai",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    void import("@/lib/agents/sentimentService").then(({ sentimentService }) => {
      sentimentService.startPolling();
    });
  }, []);

  useEffect(() => {
    void registerPWA();
    void initSentry();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <MaintenanceGate>
          <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden">
            <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
              <Outlet />
            </div>
            <SiteFooter />
          </div>
          <InstallPrompt />
          <CookieConsentBanner />
        </MaintenanceGate>
        <Toaster theme="dark" position="top-right" richColors />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
